import * as CL from '@design-system/component-library';
import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  elisaDevicesServiceMsg,
  monthlyChargesMsg,
  oneTimePaymentMsg,
  productsMsg,
  purchasedDeviceMsg,
  statusMsg,
  t,
  typeMsg,
} from '../../common/i18n/index.js';
import { excludeDeliveryFees } from '../../common/utils/orderUtils';
import { getEmployeePeriodicPriceAsText, getPriceAsText } from '../../common/utils/priceUtils';
import { Status } from '../Status/Status.js';
import { getDeliveryOrderStatusInfo } from './CustomerOrderDetailsUtil.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { CustomerOrder } from '../../generated/api/models.js';

interface CustomerOrderOrderedProductsListParams {
  pathTemplate: string;
  customerOrder: CustomerOrder;
  isApprover: boolean;
  mdmId?: string;
}

enum DeliveryOrderFields {
  DELIVERY_ORDER_PRODUCT = 'deliveryOrderProduct',
  DELIVERY_ORDER_RECURRING_PRICE = 'created',
  DELIVERY_ORDER_ONE_TIME_PRICE = 'deliveryOrderContactNames',
  DELIVERY_ORDER_TYPE = 'deliveryOrderType',
  DELIVERY_ORDER_STATE = 'deliveryOrders',
}

const columns = (): CLTableColumn[] => [
  {
    key: DeliveryOrderFields.DELIVERY_ORDER_PRODUCT,
    label: t.IY4Z(productsMsg),
  },
  {
    key: DeliveryOrderFields.DELIVERY_ORDER_RECURRING_PRICE,
    label: t.P6BC(monthlyChargesMsg),
  },
  {
    key: DeliveryOrderFields.DELIVERY_ORDER_ONE_TIME_PRICE,
    label: t.GOBY(oneTimePaymentMsg),
  },
  {
    key: DeliveryOrderFields.DELIVERY_ORDER_TYPE,
    label: t.F9G1(typeMsg),
  },
  {
    key: DeliveryOrderFields.DELIVERY_ORDER_STATE,
    label: t.ASQT(statusMsg),
  },
];

const filteredColumns = (isApprover: boolean) =>
  columns().filter(column => !(column.key === DeliveryOrderFields.DELIVERY_ORDER_ONE_TIME_PRICE && !isApprover));

const OrderedProductsTable = (props: CustomerOrderOrderedProductsListParams) => {
  const { isApprover, customerOrder, mdmId, pathTemplate } = props;

  const tableRows = customerOrder.deliveryOrders.filter(excludeDeliveryFees).map(deliveryOrder => ({
    [DeliveryOrderFields.DELIVERY_ORDER_PRODUCT]: (
      <Link
        to={
          generatePath(pathTemplate, {
            orderId: customerOrder.customerOrderDisplayId,
            deliveryId: deliveryOrder.deliveryOrderDisplayId,
          }) + (mdmId ? `?mdmId=${mdmId}` : '')
        }
      >
        <div className={dsClass.COLOR_BLUE_600}>{deliveryOrder.deliveryOrderName}</div>
        {deliveryOrder.replacedSubscriptionDisplayId && (
          <div className={`${dsClass.TEXT_S} ${dsClass.COLOR_BLACK}`}>{t.OL3F('Replacement')}</div>
        )}
      </Link>
    ),
    [DeliveryOrderFields.DELIVERY_ORDER_RECURRING_PRICE]: (
      <>
        {isApprover
          ? getPriceAsText(
              deliveryOrder.monthlyRecurringCharge,
              deliveryOrder.monthlyRecurringListCharge,
              true,
              '',
              deliveryOrder.billingPeriod
            ) || '—'
          : getEmployeePeriodicPriceAsText(deliveryOrder.employeeMonthlyRecurringCharge)}
      </>
    ),
    [DeliveryOrderFields.DELIVERY_ORDER_ONE_TIME_PRICE]: (
      <>{getPriceAsText(deliveryOrder.oneTimeCharge, deliveryOrder.oneTimeListCharge) || '—'}</>
    ),
    [DeliveryOrderFields.DELIVERY_ORDER_TYPE]: (
      <>{deliveryOrder.eppOrder ? t.JJM2(elisaDevicesServiceMsg) : t.QL8F(purchasedDeviceMsg)}</>
    ),
    [DeliveryOrderFields.DELIVERY_ORDER_STATE]: (
      <Status
        textAfterBadge={getDeliveryOrderStatusInfo(deliveryOrder.status).text}
        color={getDeliveryOrderStatusInfo(deliveryOrder.status).color}
      />
    ),
    active: !!deliveryOrder.replacedSubscriptionDisplayId,
  }));

  return (
    <div className="of-table-wrapper">
      <CL.Table
        columns={filteredColumns(isApprover)}
        hideHeadersOnMobile={false}
        rows={tableRows || []}
        tableType="bordered"
        hover={true}
      />
    </div>
  );
};

export const CustomerOrderOrderedProductsList = (props: CustomerOrderOrderedProductsListParams) => (
  <>
    <h3 className={`${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_4}`}>{t.TBES('Ordered products')}</h3>
    <OrderedProductsTable {...props} />
  </>
);
